import { NestApiType, nestCall } from '@/composables/nestApi';
import { FlowStructureDto, FlowStructureType } from '@/types/flow-structure';
import { CampaignsBulkStatusEnum, TypeCampaignEnum, IntervalDateEnum } from '@/types';
import type { HistoryEntry } from '@/composables/flows/useFlowHistory';
import moment from 'moment';

interface FlowStructureResponse {
  statusCode: number;
  data: FlowStructureDto;
}

interface UpdateStatusResponse {
  statusCode: number;
  data: { success: boolean };
}

interface SaveBulkCampaignResponse {
  statusCode: number;
  data: { success: boolean; id?: number };
}

interface SaveAutomationCampaignResponse {
  statusCode: number;
  data: { success: boolean; campaignId?: number; isNewVersion?: boolean };
}

export interface VersionConflictResult {
  hasConflict: boolean;
  localDate?: string;
  remoteDate?: string;
  remoteFlow?: FlowStructureDto;
}

export interface ScheduleBulkCampaignDto {
  id: number;
  time_sending: string;
  perfect_timing: number;
  slowdown: number;
  interval_between_sends: string;
  commercial_campaign: number;
}

interface ScheduleBulkResponse {
  statusCode: number;
  data: { success: boolean };
}

export async function fetchFlowStructure(
  type: FlowStructureType,
  campaignId: number,
): Promise<FlowStructureDto> {
  const segment = type === TypeCampaignEnum.AUTOMATION ? 'automations' : 'bulk';
  const response = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `/campaigns/${segment}/${campaignId}/flow/structure`,
  );

  const result = response as FlowStructureResponse | { error?: any } | null;

  if (!result) {
    throw new Error('Unable to load flow structure.');
  }

  if ('error' in result && result.error) {
    throw new Error(result.error.message ?? 'Unable to load flow structure.');
  }

  if (!('data' in result) || !result.data) {
    throw new Error('Invalid flow structure response.');
  }

  return result.data;
}

/**
 * Récupère la vue arbre stats d'un workflow (compteurs par step) avec
 * fenêtre de date et filtre contact optionnel.
 */
export interface WorkflowStatsTreeFilter {
  idStatsSend?: number;
  idShopContact?: number;
  idShopVisitor?: number;
  email?: string;
  // Cible une instance précise du workflow quand le contact a déclenché
  // le scénario plusieurs fois (cf. availableInstances dans la response).
  idWorkflowInstance?: number;
}

export interface WorkflowInstanceSummary {
  idWorkflow: number;
  firstEnteredAt: string;
  lastExitedAt: string;
  stepCount: number;
}

export interface WorkflowStatsTreeResult {
  workflow: {
    id: number;
    name: string;
    type: 'automation' | 'bulk';
    flow: FlowStructureDto;
  };
  filterMode: 'global' | 'contact';
  contactInfo: null | {
    idShopContact: number;
    idShopVisitor?: number;
    email?: string;
    firstName?: string;
    lastName?: string;
  };
  dateRange: { start: string; end: string };
  analyticsByStep: Record<string, any>;
  // Mode contact uniquement, présent dès qu'au moins une instance est
  // résolue. Triée ASC sur firstEnteredAt (#1 = plus ancien). Le front
  // n'affiche le dropdown que si length > 1.
  availableInstances?: WorkflowInstanceSummary[];
  selectedInstanceId?: number;
}

export async function fetchWorkflowStatsTree(
  type: 'automation' | 'bulk',
  campaignId: number,
  dateStart: string,
  dateEnd: string,
  filter: WorkflowStatsTreeFilter = {},
): Promise<WorkflowStatsTreeResult> {
  const segment = type === 'automation' ? 'automations' : 'bulk';
  const params: Record<string, string | number> = {
    date_start: dateStart,
    date_end: dateEnd,
  };
  if (filter.idStatsSend) params.id_stats_send = filter.idStatsSend;
  if (filter.idShopContact) params.id_shop_contact = filter.idShopContact;
  if (filter.idShopVisitor) params.id_shop_visitor = filter.idShopVisitor;
  if (filter.email) params.email = filter.email;
  if (filter.idWorkflowInstance) params.id_workflow_instance = filter.idWorkflowInstance;

  const response = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `/campaigns/${segment}/${campaignId}/stats-tree`,
    {},
    '',
    undefined,
    true,
    params,
  );

  const result = response as { data?: WorkflowStatsTreeResult; error?: any } | null;

  if (!result) {
    throw new Error('Unable to load workflow stats tree.');
  }
  if ('error' in result && result.error) {
    throw new Error(result.error.message ?? 'Unable to load workflow stats tree.');
  }
  if (!result.data) {
    throw new Error('Invalid workflow stats tree response.');
  }
  return result.data;
}

/**
 * Extract unique operator ids from flow steps for service checks.
 * Priority: step.parameters.id (business id), fallback: step.type.
 */
export function getFlowOperatorsFromStructure(
  flowData: FlowStructureDto,
  excludeOperators: string[] = [],
): string[] {
  if (!flowData.steps || !Array.isArray(flowData.steps)) {
    return [];
  }

  const operators = new Set<string>();
  flowData.steps.forEach((step) => {
    const rawOperatorId = step?.parameters?.id ?? step?.type;
    const operatorId = typeof rawOperatorId === 'string'
      ? rawOperatorId.trim()
      : String(rawOperatorId ?? '').trim();

    if (operatorId && !excludeOperators.includes(operatorId)) {
      operators.add(operatorId);
    }
  });

  return Array.from(operators);
}

/**
 * Fetch flow structure then extract operators for service checks.
 */
export async function fetchFlowOperators(
  type: FlowStructureType,
  campaignId: number,
  excludeOperators: string[] = [],
): Promise<string[]> {
  const flowData = await fetchFlowStructure(type, campaignId);
  return getFlowOperatorsFromStructure(flowData, excludeOperators);
}

/**
 * Update the status of a bulk campaign
 */
export async function updateBulkStatus(
  campaignId: number,
  status: CampaignsBulkStatusEnum,
): Promise<boolean> {
  const response = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    '/campaigns/bulk/status',
    {},
    '',
    { id: campaignId, status },
    true,
  );

  const result = response as UpdateStatusResponse | { error?: any } | null;

  if (!result) {
    throw new Error('Unable to update bulk campaign status.');
  }

  if ('error' in result && result.error) {
    throw new Error(result.error.message ?? 'Unable to update bulk campaign status.');
  }

  return true;
}

/**
 * Update the status of an automation campaign
 */
export async function updateAutomationStatus(
  campaignId: number,
  status: string,
): Promise<boolean> {
  const response = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    '/campaigns/automations/status',
    {},
    '',
    { id: campaignId, status },
    true,
  );

  const result = response as UpdateStatusResponse | { error?: any } | null;

  if (!result) {
    throw new Error('Unable to update automation campaign status.');
  }

  if ('error' in result && result.error) {
    throw new Error(result.error.message ?? 'Unable to update automation campaign status.');
  }

  return true;
}

/**
 * Toggle sales tracking (track_sales) for an automation campaign from the listing.
 */
export async function updateAutomationTrackSales(
  campaignId: number,
  trackSales: boolean,
): Promise<boolean> {
  const response = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    '/campaigns/automations/track-sales',
    {},
    '',
    { id: campaignId, track_sales: trackSales },
    true,
  );

  const result = response as { error?: any } | null;

  if (!result) {
    throw new Error('Unable to update sales tracking.');
  }

  if ('error' in result && result.error) {
    throw new Error(result.error.message ?? 'Unable to update sales tracking.');
  }

  return true;
}

/**
 * Save a bulk campaign
 */
export async function saveBulk(
  flow: FlowStructureDto,
  settings: Record<string, any>,
): Promise<{ success: boolean; id?: number }> {
  // Remove dateModification and settings from flow before sending
  // dateModification is only used for opening validation
  // settings should be sent separately, not inside flow
  const { dateModification, settings: _flowSettings, ...flowToSave } = flow as FlowStructureDto & { settings?: unknown };

  // Create settings object with name, legend and commercial_campaign
  const settingsToSave = {
    name: settings.name,
    legend: settings.legend ?? '',
    commercial_campaign: settings.commercial_campaign ?? 1,
  };

  const response = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    '/campaigns/bulk/save',
    {},
    '',
    { flow: flowToSave, settings: settingsToSave },
    true,
  );

  const result = response as SaveBulkCampaignResponse | { error?: any } | null;

  if (!result) {
    throw new Error('Unable to save bulk campaign.');
  }

  if ('error' in result && result.error) {
    throw new Error(result.error.message ?? 'Unable to save bulk campaign.');
  }

  if (!('data' in result) || !result.data) {
    throw new Error('Invalid save response.');
  }

  return result.data;
}

/**
 * Save an automation campaign
 */
export async function saveAutomation(
  flow: FlowStructureDto,
  settings: Record<string, any>,
): Promise<{ success: boolean; campaignId?: number; isNewVersion?: boolean }> {
  // Remove dateModification and settings from flow before sending
  // dateModification is only used for opening validation
  // settings should be sent separately, not inside flow
  const { dateModification, settings: _flowSettings, ...flowToSave } = flow as FlowStructureDto & { settings?: unknown };

  // Create a copy of settings to avoid mutating the parameter
  const settingsToSave = { ...settings };
  settingsToSave.trigger_time = settings.time_unit_select && settings.time_unit_select.value ? settings.time_unit_select.value : 1;
  settingsToSave.unit_time = settings.time_unit_select && settings.time_unit_select.unit ? settings.time_unit_select.unit : 'DAY';
  delete settingsToSave.time_unit_select;

  const response = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    '/campaigns/automations/save',
    {},
    '',
    { flow: flowToSave, settings: settingsToSave },
    true,
  );

  const result = response as SaveAutomationCampaignResponse | { error?: any } | null;

  if (!result) {
    throw new Error('Unable to save automation campaign.');
  }

  if ('error' in result && result.error) {
    throw new Error(result.error.message ?? 'Unable to save automation campaign.');
  }

  if (!('data' in result) || !result.data) {
    throw new Error('Invalid save response.');
  }

  return result.data;
}

interface DuplicateCampaignResponse {
  statusCode: number;
  data: { success: boolean; id?: number };
}

/**
 * Duplicate a flow (bulk or automation campaign)
 */
export async function duplicateFlow(
  type: FlowStructureType,
  campaignId: number,
  name: string,
): Promise<{ success: boolean; id?: number }> {
  const segment = type === TypeCampaignEnum.AUTOMATION ? 'automations' : 'bulk';
  const response = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    `/campaigns/${segment}/${campaignId}/duplicate`,
    {},
    '',
    { name },
    true,
  );

  const result = response as DuplicateCampaignResponse | { error?: any } | null;

  if (!result) {
    throw new Error(`Unable to duplicate ${type} campaign.`);
  }

  if ('error' in result && result.error) {
    throw new Error(result.error.message ?? `Unable to duplicate ${type} campaign.`);
  }

  if (!('data' in result) || !result.data) {
    throw new Error('Invalid duplicate response.');
  }

  return result.data;
}

/**
 * Schedule a bulk campaign
 */
export async function scheduleBulk(
  params: ScheduleBulkCampaignDto,
): Promise<boolean> {
  const response = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    '/campaigns/bulk/schedule',
    {},
    '',
    params,
    true,
  );

  const result = response as ScheduleBulkResponse | { error?: any } | null;

  if (!result) {
    throw new Error('Unable to schedule bulk campaign.');
  }

  if ('error' in result && result.error) {
    throw new Error(result.error.message ?? 'Unable to schedule bulk campaign.');
  }

  return true;
}

interface SaveHistoryResponse {
  statusCode: number;
  data: { success: boolean };
}

/**
 * Save flow history to the database
 * @param type Flow type (automation or bulk)
 * @param campaignId Campaign ID
 * @param history Array of history entries to save
 */
export async function saveFlowHistory(
  type: FlowStructureType,
  campaignId: number,
  history: HistoryEntry[],
): Promise<boolean> {
  const segment = type === TypeCampaignEnum.AUTOMATION ? 'automations' : 'bulk';
  const response = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    `/campaigns/${segment}/${campaignId}/flow/history`,
    {},
    '',
    { history },
    true,
  );

  const result = response as SaveHistoryResponse | { error?: any } | null;

  if (!result) {
    throw new Error('Unable to save flow history.');
  }

  if ('error' in result && result.error) {
    throw new Error(result.error.message ?? 'Unable to save flow history.');
  }

  return true;
}

/**
 * Check if there's a version conflict between local and remote flow
 * @param type Flow type (automation or bulk)
 * @param campaignId Campaign ID
 * @param localDateModification Local date modification stored in localStorage
 * @returns Version conflict result
 */
export async function checkVersionConflict(
  type: FlowStructureType,
  campaignId: number,
  localDateModification: string | undefined,
): Promise<VersionConflictResult> {
  if (!localDateModification) {
    return { hasConflict: false };
  }

  try {
    const remoteFlow = await fetchFlowStructure(type, campaignId);
    const remoteDateModification = remoteFlow.dateModification;

    if (!remoteDateModification) {
      return { hasConflict: false };
    }

    // Compare dates
    const localDate = new Date(localDateModification);
    const remoteDate = new Date(remoteDateModification);

    if (localDate.getTime() !== remoteDate.getTime()) {
      return {
        hasConflict: true,
        localDate: localDateModification,
        remoteDate: remoteDateModification,
        remoteFlow,
      };
    }

    return { hasConflict: false };
  } catch (error) {
    console.error('Error checking version conflict:', error);
    return { hasConflict: false };
  }
}

/**
 * Analytics date interval configuration
 */
export interface AnalyticsDateInterval {
  interval: IntervalDateEnum;
  customDateRange?: Date[];
}

/**
 * Get date range filters for analytics based on interval
 */
function getAnalyticsDateRange(dateInterval: AnalyticsDateInterval): { dateFrom: string; dateTo: string } {
  let dateFrom;
  let dateTo = moment().format('YYYYMMDD23');

  switch (dateInterval.interval) {
    case IntervalDateEnum.LAST_DAY:
      dateFrom = moment().subtract(1, 'days').format('YYYYMMDD00');
      break;
    case IntervalDateEnum.LAST_3_DAYS:
      dateFrom = moment().subtract(3, 'days').format('YYYYMMDD00');
      break;
    case IntervalDateEnum.LAST_7_DAYS:
      dateFrom = moment().subtract(7, 'days').format('YYYYMMDD00');
      break;
    case IntervalDateEnum.LAST_14_DAYS:
      dateFrom = moment().subtract(14, 'days').format('YYYYMMDD00');
      break;
    case IntervalDateEnum.LAST_30_DAYS:
      dateFrom = moment().subtract(30, 'days').format('YYYYMMDD00');
      break;
    case IntervalDateEnum.LAST_3_MONTHS:
      dateFrom = moment().subtract(3, 'months').format('YYYYMMDD00');
      break;
    case IntervalDateEnum.LAST_12_MONTHS:
      dateFrom = moment().subtract(12, 'months').format('YYYYMMDD00');
      break;
    case IntervalDateEnum.CUSTOM_DATE_RANGE:
      if (dateInterval.customDateRange && dateInterval.customDateRange.length === 2) {
        dateFrom = moment(dateInterval.customDateRange[0]).format('YYYYMMDD00');
        dateTo = moment(dateInterval.customDateRange[1]).format('YYYYMMDD23');
      } else {
        dateFrom = moment().subtract(14, 'days').format('YYYYMMDD00');
      }
      break;
    default:
      dateFrom = moment().subtract(14, 'days').format('YYYYMMDD00');
      break;
  }

  return { dateFrom, dateTo };
}

/**
 * Fetch workflow analytics statistics
 * @param idShop Shop ID
 * @param typeCampaign Campaign type (automation or bulk)
 * @param idCampaign Campaign ID
 * @param dateInterval Date interval configuration
 * @returns Analytics data or null if no data
 */
export async function fetchWorkflowAnalytics(
  idShop: number,
  typeCampaign: TypeCampaignEnum,
  idCampaign: number,
  dateInterval: AnalyticsDateInterval,
): Promise<Record<string, any> | null> {
  const filters = getAnalyticsDateRange(dateInterval);

  // Validate filters
  if (!filters.dateFrom || filters.dateFrom === 'Invalid date') {
    return null;
  }

  const postData = {
    idCampaign,
    idShop,
    typeCampaign,
    filters,
  };

  try {
    const result = await nestCall(
      'post',
      NestApiType.DEFAULT,
      '/workflow/getStats',
      {},
      '',
      postData,
      true,
    );

    if (result && result.success && result.statistics) {
      return result.statistics;
    }

    return null;
  } catch (error) {
    console.error('Error fetching workflow analytics:', error);
    return null;
  }
}
